import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { FileText, FileSpreadsheet, ArrowLeft, RefreshCw, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { getAuthHeaders } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + "/api";

// Document colors
const DOC_COLORS = {
  vendor: { border: "border-orange-500/30", bg: "bg-orange-500/5", text: "text-orange-400", headerBg: "bg-orange-500/10", badge: "bg-orange-500/90" },
  ivalue: { border: "border-blue-500/30", bg: "bg-blue-500/5", text: "text-blue-400", headerBg: "bg-blue-500/10", badge: "bg-blue-500/90" },
  partner_po: { border: "border-emerald-500/30", bg: "bg-emerald-500/5", text: "text-emerald-400", headerBg: "bg-emerald-500/10", badge: "bg-emerald-500/90" }
};

const SLOT_LABELS = { vendor: "Vendor Quotation", ivalue: "iValue Quotation", partner_po: "Partner PO" };
const isSpreadsheetFile = (filename) => /\.(xlsx|xls)$/i.test(filename || "");

function DocumentPane({ slot, files, activeIdx, onSelectIdx, fileUrl, highlightsCount, colors }) {
  const active = files[activeIdx];

  return (
    <div className="flex flex-col">
      <div className={`flex items-center justify-between mb-2 px-3 py-2 rounded-t-lg ${colors.headerBg}`}>
        <h3 className={`text-sm font-semibold ${colors.text}`}>{SLOT_LABELS[slot]}</h3>
        <span className="text-xs text-zinc-500 truncate max-w-[140px]">{active?.filename || ""}</span>
      </div>

      {files.length > 1 && (
        <div className="flex gap-1 mb-2 flex-wrap">
          {files.map((f, i) => (
            <button
              key={i}
              onClick={() => onSelectIdx(i)}
              className={`text-xs px-2 py-0.5 rounded ${
                i === activeIdx ? `${colors.badge} text-white` : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
              }`}
              title={f.filename}
            >
              {isSpreadsheetFile(f.filename) ? <FileSpreadsheet size={10} className="inline mr-1" /> : <FileText size={10} className="inline mr-1" />}
              {i + 1}
            </button>
          ))}
        </div>
      )}

      <Card className={`flex-1 ${colors.bg} ${colors.border} overflow-hidden`}>
        <div className="h-full relative">
          {!active ? (
            <div className="flex items-center justify-center h-full text-zinc-500">
              <div className="text-center">
                <FileText size={48} className="mx-auto mb-2 opacity-50" />
                <p>Document not available</p>
                <p className="text-xs mt-1">Uploaded before file viewer</p>
              </div>
            </div>
          ) : !fileUrl ? (
            <div className="flex items-center justify-center h-full text-zinc-500">
              <RefreshCw className="animate-spin" size={24} />
            </div>
          ) : isSpreadsheetFile(active.filename) ? (
            <div className="flex items-center justify-center h-full text-zinc-400">
              <div className="text-center">
                <FileSpreadsheet size={48} className="mx-auto mb-3 opacity-70" />
                <p className="text-sm">{active.filename}</p>
                <p className="text-xs text-zinc-600 mt-1 mb-3">Spreadsheets can't be previewed inline</p>
                <a href={fileUrl} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="secondary" className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200">
                    <Download size={14} className="mr-1" /> Open / Download
                  </Button>
                </a>
              </div>
            </div>
          ) : (
            <iframe src={fileUrl} className="w-full h-full border-0" title={SLOT_LABELS[slot]} />
          )}
          {highlightsCount > 0 && (
            <div className={`absolute top-2 right-2 ${colors.badge} text-white text-xs px-2 py-1 rounded`}>
              {highlightsCount} findings
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function FindingsList({ vendorDisc, poDisc }) {
  const vItems = (vendorDisc || []).slice(0, 3);
  const pItems = (poDisc || []).slice(0, 3);
  
  return (
    <div className="mt-4">
      <h3 className="text-sm font-semibold text-zinc-200 mb-2">Highlighted Findings</h3>
      <div className="grid grid-cols-3 gap-4">
        {vItems.map((d, i) => (
          <div key={`v-${i}`} className="p-2 rounded bg-orange-500/10 border border-orange-500/20 text-xs">
            <span className="text-orange-400 font-medium">{d.field}</span>
            <p className="text-zinc-400 mt-1">{d.description}</p>
          </div>
        ))}
        {pItems.map((d, i) => (
          <div key={`p-${i}`} className="p-2 rounded bg-emerald-500/10 border border-emerald-500/20 text-xs">
            <span className="text-emerald-400 font-medium">{d.field}</span>
            <p className="text-zinc-400 mt-1">{d.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PDFViewerPage() {
  const [comp, setComp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState({ vendor: 0, ivalue: 0, partner_po: 0 });
  const [blobUrls, setBlobUrls] = useState({ vendor: null, ivalue: null, partner_po: null });
  const navigate = useNavigate();
  const compId = window.location.pathname.split("/viewer/")[1];

  useEffect(() => {
    if (compId) {
      axios.get(`${API}/comparisons/${compId}`, { headers: getAuthHeaders() })
        .then((res) => setComp(res.data))
        .catch(() => { toast.error("Failed to load"); navigate("/"); })
        .finally(() => setLoading(false));
    }
  }, [compId, navigate]);

  // Fetch each active file via an authenticated request instead of putting the
  // bearer token in the URL (iframe/anchor URLs would otherwise leak it into
  // browser history, proxy logs, and referrers).
  useEffect(() => {
    if (!comp) return;
    let cancelled = false;
    const createdUrls = [];
    ["vendor", "ivalue", "partner_po"].forEach((slot) => {
      const files = comp.files?.[slot] || [];
      const idx = activeIdx[slot];
      if (!files[idx]) return;
      axios
        .get(`${API}/comparisons/${compId}/files/${slot}/${idx}`, {
          headers: getAuthHeaders(),
          responseType: "blob",
        })
        .then((res) => {
          if (cancelled) return;
          const url = URL.createObjectURL(res.data);
          createdUrls.push(url);
          setBlobUrls((prev) => ({ ...prev, [slot]: url }));
        })
        .catch(() => {});
    });
    return () => {
      cancelled = true;
      createdUrls.forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comp, activeIdx, compId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }
  if (!comp) return null;

  const filesFor = (slot) => comp.files?.[slot] || [];
  const selectIdx = (slot, idx) => setActiveIdx((prev) => ({ ...prev, [slot]: idx }));

  const getHighlights = (docType) => {
    const highlights = [];
    const vendorDisc = comp.vendor_vs_ivalue_discrepancies || [];
    const poDisc = comp.ivalue_vs_po_discrepancies || [];
    
    if (docType === "vendor" || docType === "ivalue") {
      vendorDisc.forEach(d => {
        highlights.push({ field: d.field, severity: d.severity, category: d.category });
      });
    }
    if (docType === "ivalue" || docType === "partner_po") {
      poDisc.forEach(d => {
        highlights.push({ field: d.field, severity: d.severity, category: d.category });
      });
    }
    return highlights;
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="min-h-screen p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/result/${compId}`)} className="text-zinc-400 hover:text-white">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-white">
              PDF Viewer - <span className="text-blue-400">{comp.quotation_id}</span>
              <span className="text-zinc-500 text-sm ml-2">v{comp.version}</span>
            </h1>
            <p className="text-zinc-500 text-sm">Side-by-side document comparison</p>
          </div>
        </div>
      </div>

      <div className="mb-4 p-3 rounded-lg bg-zinc-900/50 border border-white/5">
        <div className="flex items-center gap-4 text-sm">
          <span className="text-zinc-400">Findings:</span>
          {(comp.vendor_vs_ivalue_discrepancies || []).length > 0 && (
            <span className="px-2 py-1 rounded bg-orange-500/20 text-orange-400 text-xs">
              <span className="text-orange-400">Vendor</span> vs <span className="text-blue-400">iValue</span>: {(comp.vendor_vs_ivalue_discrepancies || []).length}
            </span>
          )}
          {(comp.ivalue_vs_po_discrepancies || []).length > 0 && (
            <span className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 text-xs">
              <span className="text-blue-400">iValue</span> vs <span className="text-emerald-400">Partner PO</span>: {(comp.ivalue_vs_po_discrepancies || []).length}
            </span>
          )}
          {comp.total_discrepancies === 0 && (
            <span className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 text-xs">
              No discrepancies found
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4" style={{ height: "calc(100vh - 180px)" }}>
        {["vendor", "ivalue", "partner_po"].map((slot) => (
          <DocumentPane
            key={slot}
            slot={slot}
            files={filesFor(slot)}
            activeIdx={activeIdx[slot]}
            onSelectIdx={(idx) => selectIdx(slot, idx)}
            fileUrl={blobUrls[slot]}
            highlightsCount={getHighlights(slot).length}
            colors={DOC_COLORS[slot]}
          />
        ))}
      </div>

      {comp.total_discrepancies > 0 && (
        <FindingsList 
          vendorDisc={comp.vendor_vs_ivalue_discrepancies}
          poDisc={comp.ivalue_vs_po_discrepancies}
        />
      )}
    </motion.div>
  );
}
